import { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { getSenderPerspectiveLabel } from '@/lib/senderPerspective';
import { CinematicBurst } from './CinematicBurst';
import { RevealPhotos } from './RevealPhotos';
import { useRevealViewport, PreparingIndicator } from '../useRevealViewport';
import type { FullCanvasSceneProps } from './types';
import { STYLE_ALIGNMENT } from './types';
import { AdaptiveMessageBody } from '../cinematic/adaptive/AdaptiveMessageBody';
import { getReadingRevealCompleteMs } from './readingDwell';

import { DepthRevealLayer } from '../cinematic/DepthRevealLayer';
const EASE = [0.22, 0.61, 0.36, 1] as const;

const STAR_COLORS = [
  '#ffffff', '#e0e7ff', '#c7d2fe', '#a5b4fc', '#818cf8',
  '#6366f1', '#fef08a', '#fde68a', '#fcd34d', '#fbbf24',
  '#c4b5fd', '#ddd6fe', '#e9d5ff', '#f5d0fe', '#fbcfe8',
];

export function FullCanvasConstellation({ recipientName, relationship, message, photoUrls, publishedDate, onComplete, config, startAtMessage = false }: FullCanvasSceneProps) {
  const [phase, setPhase] = useState<'void' | 'burst' | 'constellation' | 'text' | 'done'>(startAtMessage ? 'text' : 'void');
  const [burstFired, setBurstFired] = useState(false);
  const { isMobile, burstOriginY, timingMultiplier: tm, particleScale } = useRevealViewport();

  const t = (ms: number) => ms * tm;

  useEffect(() => {
    if (startAtMessage) {
      const tDone = setTimeout(() => { setPhase('done'); onComplete(); }, getReadingRevealCompleteMs({
        message,
        mode: config?.style.readingRhythm ?? 'paragraph',
        pacing: config?.pacing ?? 1,
        startDelay: 1.2,
      }));
      return () => clearTimeout(tDone);
    }
    const t1 = setTimeout(() => { setPhase('burst'); setBurstFired(true); }, t(4000));
    const t2 = setTimeout(() => setPhase('constellation'), t(7000));
    const t3 = setTimeout(() => setPhase('text'), t(11500));
    const t4 = setTimeout(() => { setPhase('done'); onComplete(); }, t(17500));
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); clearTimeout(t4); };
  }, [config?.pacing, config?.style.readingRhythm, message, onComplete, startAtMessage, tm]);

  const isPostBurst = phase !== 'void';
  const isConstellationOrLater = phase === 'constellation' || phase === 'text' || phase === 'done';
  const isTextVisible = phase === 'text' || phase === 'done';

  const bgStarCount = Math.round(100 * particleScale);
  const bgStars = useMemo(() =>
    Array.from({ length: bgStarCount }).map((_, i) => ({
      x: Math.random() * 100, y: Math.random() * 100,
      size: 1 + Math.random() * 2.5, delay: t(4500) / 1000 + Math.random() * 3,
      duration: 2 + Math.random() * 3, brightness: 0.2 + Math.random() * 0.6,
    })),
  [bgStarCount, tm]);

  const constStarCount = Math.round(28 * particleScale);
  const constellationStars = useMemo(() =>
    Array.from({ length: constStarCount }).map((_, i) => ({
      x: 10 + Math.random() * 80, y: 15 + Math.random() * 70,
      delay: t(7000) / 1000 + i * 0.12, size: 2 + Math.random() * 3.5,
    })),
  [constStarCount, tm]);

  return (
    <div className={`min-h-[100dvh] sm:min-h-screen relative ${isTextVisible ? 'overflow-y-auto overflow-x-hidden' : 'overflow-hidden'}`} style={{
      background: 'linear-gradient(180deg, #020510 0%, #050714 20%, #0a0e2a 50%, #0f1535 75%, #080c1f 100%)',
    }}>
      {/* Mobile preparing indicator */}
      {isMobile && <PreparingIndicator visible={phase === 'void'} color="rgba(180,200,255,0.6)" />}

      {phase === 'void' && (
        <>
          <motion.div className="absolute inset-0 pointer-events-none"
            style={{ background: `radial-gradient(ellipse at 40% ${burstOriginY - 10}%, rgba(80,60,180,0.06) 0%, transparent 40%)` }}
            animate={{ opacity: [0, 0.8, 0.2, 0.8], scale: [0.8, 1.2, 0.9, 1.2] }}
            transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
          />
          <motion.div className="absolute inset-0 pointer-events-none"
            style={{ background: `radial-gradient(ellipse at 65% ${burstOriginY + 10}%, rgba(40,80,160,0.04) 0%, transparent 35%)` }}
            animate={{ opacity: [0, 0.6, 0.1, 0.6] }}
            transition={{ duration: 2.5, delay: 0.5, repeat: Infinity, ease: 'easeInOut' }}
          />
          {[0, 1, 2, 3].map(i => (
            <motion.div key={`pre-${i}`} className="absolute rounded-full pointer-events-none"
              style={{
                width: isMobile ? 4 : 2, height: isMobile ? 4 : 2,
                left: `${30 + i * 12}%`, top: `${(burstOriginY - 10) + (i % 2) * 15}%`,
                background: 'white',
                boxShadow: '0 0 8px 3px rgba(180,200,255,0.3)',
              }}
              animate={{ opacity: [0, 0.5, 0] }}
              transition={{ duration: 2, delay: i * 0.5, repeat: Infinity, ease: 'easeInOut' }}
            />
          ))}
        </>
      )}

      <motion.div className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse at 35% 25%, rgba(80,60,180,0.18) 0%, transparent 45%), radial-gradient(ellipse at 70% 65%, rgba(40,80,160,0.12) 0%, transparent 45%), radial-gradient(ellipse at 50% 50%, rgba(60,40,120,0.08) 0%, transparent 60%)',
        }}
        initial={{ opacity: 0 }}
        animate={{ opacity: isPostBurst ? [0.5, 0.9, 0.5] : 0 }}
        transition={isPostBurst ? { duration: 8, repeat: Infinity, ease: 'easeInOut' } : { duration: 0.5 }}
      />

      <CinematicBurst active={burstFired} count={Math.round(75 * particleScale)} colors={STAR_COLORS} shape="star" originX={50} originY={burstOriginY} spread={isMobile ? 45 : 60} ambientCount={Math.round(20 * particleScale)} glowIntensity={4} />

      {isPostBurst && bgStars.map((star, i) => (
        <motion.div key={`bg-${i}`} className="absolute rounded-full pointer-events-none"
          style={{
            width: star.size, height: star.size, left: `${star.x}%`, top: `${star.y}%`,
            background: 'white',
            boxShadow: `0 0 ${star.size * 2}px ${star.size}px rgba(200,210,255,0.3)`,
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: [0, star.brightness, star.brightness * 0.3, star.brightness] }}
          transition={{ duration: star.duration, delay: star.delay, repeat: Infinity, ease: 'easeInOut' }}
        />
      ))}

      {isConstellationOrLater && constellationStars.map((star, i) => (
        <motion.div key={`const-${i}`} className="absolute pointer-events-none"
          style={{ left: `${star.x}%`, top: `${star.y}%` }}
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: [0, 0.9, 0.5, 0.9], scale: [0, 1.3, 0.9, 1.1] }}
          transition={{ duration: 2 + (i % 3) * 0.5, delay: star.delay - t(7000) / 1000, repeat: Infinity, ease: 'easeInOut' }}
        >
          <div className="rounded-full" style={{
            width: star.size, height: star.size, background: 'white',
            boxShadow: `0 0 ${star.size * 4}px ${star.size * 1.5}px rgba(180,200,255,0.6), 0 0 ${star.size * 8}px ${star.size * 3}px rgba(120,140,255,0.2)`,
          }} />
        </motion.div>
      ))}

      {isConstellationOrLater && (
        <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ zIndex: 5 }}>
          {constellationStars.map((star, i) => {
            const next = constellationStars[i + 1];
            if (!next) return null;
            const dist = Math.hypot(next.x - star.x, next.y - star.y);
            if (dist > 25) return null;
            return (
              <motion.line key={`line-${i}`}
                x1={`${star.x}%`} y1={`${star.y}%`}
                x2={`${next.x}%`} y2={`${next.y}%`}
                stroke="rgba(180,200,255,0.25)" strokeWidth={0.5}
                initial={{ pathLength: 0, opacity: 0 }}
                animate={{ pathLength: 1, opacity: 0.3 }}
                transition={{ delay: (star.delay - t(7000) / 1000) + 0.5, duration: 1.0, ease: EASE as any }}
              />
            );
          })}
        </svg>
      )}

      {isPostBurst && [0, 1, 2].map(i => (
        <motion.div key={`shoot-${i}`} className="absolute pointer-events-none z-10"
          style={{
            width: 100, height: 2,
            background: 'linear-gradient(to right, rgba(200,210,255,0.9), rgba(200,210,255,0.4), transparent)',
            top: `${10 + i * 25}%`, transform: 'rotate(-15deg)', borderRadius: 2,
            boxShadow: '0 0 12px 4px rgba(200,210,255,0.3)',
          }}
          initial={{ left: '-10%', opacity: 0 }}
          animate={{ left: ['-10%', '120%'], opacity: [0, 1, 0] }}
          transition={{ delay: t(7000) / 1000 + i * 5, duration: 0.8, repeat: Infinity, repeatDelay: 8 + i * 3, ease: EASE as any }}
        />
      ))}

      <motion.div
        className="relative z-20 py-12 sm:py-16 md:py-20 px-4 sm:px-6 md:px-8"
        initial={{ opacity: 0 }} animate={{ opacity: isTextVisible ? 1 : 0 }}
        transition={{ duration: startAtMessage ? 1.9 : 1.5, ease: EASE as any }}
      >
        <div className="max-w-2xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={isTextVisible ? { opacity: 1, y: 0 } : {}} transition={{ duration: 1.2, ease: EASE as any }}>
            <p className="text-xs uppercase tracking-[0.3em] mb-3" style={{ color: 'rgba(180,200,255,0.5)' }}>
              A message written in the stars
            </p>
            <h1 className="font-serif text-3xl md:text-5xl mb-4 tracking-wide" style={{
              color: 'rgba(220,225,255,0.95)', textShadow: '0 0 30px rgba(140,160,255,0.3)',
            }}>For {recipientName}</h1>
            <div className="flex items-center justify-center gap-4 mb-8">
              <div className="w-16 h-px" style={{ background: 'rgba(140,160,255,0.2)' }} />
              <motion.div className="w-2 h-2 rounded-full" style={{ background: 'rgba(180,200,255,0.5)' }}
                animate={{ boxShadow: ['0 0 0 0 rgba(140,160,255,0)', '0 0 15px 6px rgba(140,160,255,0.5)', '0 0 0 0 rgba(140,160,255,0)'] }}
                transition={{ duration: 2.5, repeat: Infinity }}
              />
              <div className="w-16 h-px" style={{ background: 'rgba(140,160,255,0.2)' }} />
            </div>
          </motion.div>

          <RevealPhotos photoUrls={photoUrls} visible={isTextVisible} delay={0.8} borderColor="rgba(180,200,255,0.4)" shadowColor="rgba(100,120,255,0.2)" />

          <div className="mb-12">
            <AdaptiveMessageBody
              message={message}
              mode={config?.style.readingRhythm ?? 'paragraph'}
              pacing={config?.pacing ?? 1}
              visible={isTextVisible}
              startDelay={1.2}
              alignment={config ? STYLE_ALIGNMENT[config.styleId] : 'centered'}
              textColorClass=""
              emphasisGlow="rgba(180,210,255,0.5)"
              typography={config?.typography ?? "serif-classic"}
            />
          </div>

          <motion.div initial={{ opacity: 0 }} animate={isTextVisible ? { opacity: 1 } : {}} transition={{ delay: 3.5, duration: 1.0 }}>
            <p className="text-lg font-serif italic" style={{ color: 'rgba(180,200,255,0.6)' }}>Eternally yours,</p>
            <p className="text-xl font-serif mt-1" style={{ color: 'rgba(220,225,255,0.8)' }}>
              — From your {getSenderPerspectiveLabel(relationship)}
            </p>
            <div className="mt-10 pt-6" style={{ borderTop: '1px solid rgba(140,160,255,0.1)' }}>
              <p className="text-xs tracking-wider" style={{ color: 'rgba(140,160,255,0.3)' }}>Say It NOW • While They're Still Here</p>
              {publishedDate && <p className="text-xs mt-1" style={{ color: 'rgba(140,160,255,0.2)' }}>Shared on {publishedDate}</p>}
            </div>
          </motion.div>
        </div>
      </motion.div>
      <DepthRevealLayer active={isTextVisible} tint="rgba(180,210,255,0.6)" glow="rgba(220,235,255,0.55)" />
    </div>
  );
}
